/**
 * EKYCModal - GMO顔認証eKYC開始モーダル
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError, EKYCApplicationResponse } from '../../lib/api-client';
import { AnalyticsLogger } from '../../lib/analytics';
import { isSmartphoneForEkyc } from '../../lib/device';

interface EKYCModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: number;
    onVerificationComplete: (result: EKYCApplicationResponse) => void;
}

export function EKYCModal({ isOpen, onClose }: EKYCModalProps) {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isSupportedDevice = isSmartphoneForEkyc();

    useEffect(() => {
        if (isOpen) {
            AnalyticsLogger.getInstance().trackEkycStart();
            setError(null);
        }
    }, [isOpen]);

    const handleClose = () => {
        if (!isSubmitting) {
            setError(null);
            onClose();
        }
    };

    const handleStart = async () => {
        if (!isSupportedDevice) {
            setError(t('ekyc.error.smartphone_required', 'GMO本人確認はスマートフォンから行ってください。PC・タブレットでは開始できません。'));
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const result = await apiClient.startEKYC();
            if (!result.provider_redirect_url) {
                throw new Error(t('ekyc.error.redirect_missing', '本人確認画面のURLを取得できませんでした'));
            }

            AnalyticsLogger.getInstance().trackEkycComplete('pending');
            window.location.assign(result.provider_redirect_url);
        } catch (err) {
            AnalyticsLogger.getInstance().trackEkycComplete('error');
            console.error('Failed to start eKYC:', err);
            if (err instanceof ApiError) {
                setError(err.detail);
            } else if (err instanceof Error) {
                setError(err.message || t('ekyc.error.apply_failed', '申請に失敗しました'));
            } else {
                setError(t('ekyc.error.apply_failed', '申請に失敗しました'));
            }
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={(event) => event.target === event.currentTarget && handleClose()}
        >
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
                <div className="bg-pink-500 px-6 py-4 rounded-t-lg">
                    <h2 className="text-lg font-bold text-white">
                        {t('ekyc.modal.title', '本人確認')}
                    </h2>
                </div>

                <div className="p-6 space-y-6">
                    <div className="text-gray-700 text-sm space-y-3">
                        <p>
                            {t('ekyc.modal.description', 'GMO顔認証eKYCの画面に移動して本人確認を行います。')}
                        </p>
                        <ul className="list-disc list-inside text-gray-600 ml-2 space-y-1">
                            <li>{t('ekyc.modal.prepare_id', '本人確認書類をご用意ください')}</li>
                            <li>{t('ekyc.modal.prepare_camera', 'スマートフォンのカメラを使用します')}</li>
                            <li>{t('ekyc.modal.after_return', '完了後、管理者レビュー待ちになります')}</li>
                        </ul>
                    </div>

                    {!isSupportedDevice && (
                        <div className="p-3 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded text-sm">
                            {t('ekyc.message.smartphone_required', '本人確認はスマートフォンから行ってください。PC・タブレットではGMO本人確認画面を開始できません。')}
                        </div>
                    )}

                    {error && (
                        <div className="p-3 bg-red-100 border border-red-300 text-red-700 rounded text-sm">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={handleClose}
                            className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded transition-colors"
                            disabled={isSubmitting}
                        >
                            {t('common.cancel', 'キャンセル')}
                        </button>
                        <button
                            onClick={handleStart}
                            disabled={isSubmitting || !isSupportedDevice}
                            className="flex-1 py-2 px-4 bg-pink-500 hover:bg-pink-600 text-white font-semibold rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting
                                ? t('ekyc.button.redirecting', '移動中...')
                                : t('ekyc.button.start', '本人確認を始める')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
